import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import axios from 'axios'
import VueLazyload from 'vue3-lazyload'
import VueAxiosPlugin from './customer/plugin/vue-axios'
import { generateApolloProvider } from './customer/provider/vue-apollo'
import loadingImgUrl from '../images/lazyload/loading.png'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import smoothscroll from 'smoothscroll-polyfill'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import utc from 'dayjs/plugin/utc'
import timeZone from 'dayjs/plugin/timezone'
import { debounce } from 'lodash'
import { setUpVeeValidate } from '~/src/js/vee-validate'

export default {
  start: () => {
    smoothscroll.polyfill()

    dayjs.extend(isSameOrBefore)
    dayjs.extend(isSameOrAfter)
    dayjs.extend(utc)
    dayjs.extend(timeZone)
    dayjs.tz.setDefault('Asia/Tokyo')

    setUpVeeValidate()

    const vueInstances = []
    const vueOptions = {}
    const apolloProvider = generateApolloProvider()

    const requireContext = import.meta.globEager([
      '@/src/js/customer/**/*.vue',
      '@/src/js/customer/**/*.js'
    ])

    Object.keys(requireContext).forEach(key => {
      const name = key
        .split('/')
        .pop()
        .split('.')
        .shift()
      vueOptions[name] = requireContext[key].default
    })

    window.addEventListener('DOMContentLoaded', () => {
      const templates = document.querySelectorAll('[data-vue]')

      for (const el of templates) {
        const app = createApp(vueOptions[el.dataset.vue])
        const pinia = createPinia()
        pinia.use(piniaPluginPersistedstate)
        app.use(apolloProvider)
        app.use(pinia)
        app.use(VueLazyload, {
          selector: 'img',
          preLoad: 1.1,
          loading: loadingImgUrl,
          error: 'https://dummyimage.com/500x500/ccc/999.png&text=error'
        })
        app.use(VueAxiosPlugin, { axios })

        app.directive('scroll', {
          beforeMount(el, binding) {
            el.addEventListener('scroll', debounce(binding.value, 100))
          },
          unmounted(el, binding) {
            el.removeEventListener('scroll', debounce(binding.value, 100))
          }
        })
        app.directive('resize', {
          beforeMount(el, binding) {
            // resize の発火頻度を調整している
            // ref: https://jp.vuejs.org/v2/guide/computed.html#%E3%82%A6%E3%82%A9%E3%83%83%E3%83%81%E3%83%A3
            // ref: https://webman-japan.com/vue-event-resize/
            window.addEventListener('resize', debounce(binding.value))
          },
          unmounted(el, binding) {
            window.removeEventListener('resize', debounce(binding.value))
          }
        })

        if (
          ['staging', 'production'].includes(process.env.SENTRY_CURRENT_ENV)
        ) {
          Sentry.init({
            app,
            dsn:
              'https://fa22e615e4154f5781a29b6517231694@o100084.ingest.sentry.io/5202269',
            integrations: [
              new Integrations.Dedupe(),
              new Integrations.ExtraErrorData({ depth: 3 })
            ],
            environment: process.env.SENTRY_CURRENT_ENV
          })
        }

        app.mount(el)
        vueInstances.push(app)
      }
    })

    window.addEventListener('unload', () => {
      for (const app of vueInstances) {
        app.unmount()
      }
      vueInstances.length = 0
    })
  }
}
